import * as zod from 'zod';

import { Expression } from '@sb/routine-runner';

/**
 * The shape of a condition row as it is stored in a step's condition JSON.
 *
 * Each variable kind requires only the fields the routine runner reads to
 * find the value. Display fields are optional: the routine DSL does not write
 * them, and the editor stores a snapshot of the live variable it picked, which
 * goes stale when that variable is renamed. Resolve a stored variable against
 * the live variable lists to display it.
 */
const EnumValue = zod.object({
  value: zod.union([zod.string(), zod.number(), zod.boolean()]),
  displayName: zod.string(),
});

const displayFields = {
  displayName: zod.string().optional(),
  unitLabel: zod.string().optional(),
  values: zod.array(EnumValue).optional(),
  variableKind: zod
    .enum([
      'boolean',
      'composite',
      'enum',
      'expression',
      'jointPose',
      'number',
      'string',
      'function',
    ])
    .optional(),
  userIdentifier: zod.string().optional(),
};

const variableOfKind = <Kind extends string, Identity extends zod.ZodRawShape>(
  kind: Kind,
  identity: Identity,
) =>
  zod
    .object({ kind: zod.literal(kind), ...displayFields, ...identity })
    .passthrough();

export const StoredConditionVariable = zod.discriminatedUnion('kind', [
  variableOfKind('step', {
    stepID: zod.string(),
    variableName: zod.string(),
    // The producing step's kind, which `validateConditionalSteps` checks
    // against the live step. The DSL fills it in from the step tree.
    stepKind: zod.string().optional(),
    stepName: zod.string().optional(),
    stepPosition: zod.number().optional(),
  }),
  variableOfKind('distance', {
    stepID: zod.string(),
    variableName: zod.string(),
  }),
  variableOfKind('environment', { variableName: zod.string() }),
  variableOfKind('ioState', {
    variableName: zod.string(),
    port: zod.number(),
  }),
  variableOfKind('sensorState', { sensorID: zod.string() }),
  variableOfKind('gripperState', { selector: zod.string() }),
  variableOfKind('equipment', {
    equipmentID: zod.string(),
    selector: zod.string(),
    unitType: zod.enum(['force', 'distance']).optional(),
  }),
  variableOfKind('cartesianPosition', { selector: zod.string() }),
  variableOfKind('jointPose', { variableName: zod.string() }),
  variableOfKind('force', {
    variableName: zod.string(),
    sensor: zod.string().optional(),
  }),
  variableOfKind('expression', {}),
]);

// `.passthrough()` keeps unknown keys at runtime but adds an index signature
// to the inferred type, which interfaces such as `RoutineVariable.*` cannot
// satisfy. The exported types drop it.
type WithoutIndexSignature<T> = T extends unknown
  ? { [K in keyof T as string extends K ? never : K]: T[K] }
  : never;

export type StoredConditionVariable = WithoutIndexSignature<
  zod.infer<typeof StoredConditionVariable>
>;

export const StoredConditionOperator = zod
  .object({
    operator: zod.enum(['==', '!=', '>', '<']),
    displayName: zod.string().optional(),
  })
  .passthrough();

export type StoredConditionOperator = WithoutIndexSignature<
  zod.infer<typeof StoredConditionOperator>
>;

// JSON drops `undefined`, so an unfilled field may be absent, not null.
export const StoredConditionRow = zod
  .object({
    variable: StoredConditionVariable.nullish(),
    operator: StoredConditionOperator.nullish(),
    value: zod
      .union([zod.string(), zod.number(), zod.boolean(), Expression])
      .nullish(),
  })
  .passthrough();

export interface StoredConditionRow {
  variable?: StoredConditionVariable | null;
  operator?: StoredConditionOperator | null;
  value?: string | number | boolean | Expression | null;
}

/** OR-groups of AND-groups of rows. */
export const StoredConditions = zod
  .array(zod.array(StoredConditionRow).min(1))
  .min(1);

export type StoredConditions = StoredConditionRow[][];

const StoredConditionGroups = zod.array(zod.array(zod.unknown()));

/** Read group structure for summaries and repairs, retaining invalid rows. */
export function parseStoredConditionGroups(json: string): unknown[][] {
  const parsed: unknown = JSON.parse(json);

  return StoredConditionGroups.parse(parsed);
}

/**
 * Parses a step's condition JSON. Throws when the string is not JSON or does
 * not match the stored shape.
 */
export function parseStoredConditions(json: string): StoredConditions {
  return StoredConditions.parse(JSON.parse(json));
}

/** Like `parseStoredConditions`, but returns null instead of throwing. */
export function safeParseStoredConditions(
  json: string,
): StoredConditions | null {
  try {
    const result = StoredConditions.safeParse(JSON.parse(json));

    return result.success ? result.data : null;
  } catch (error: unknown) {
    if (error instanceof SyntaxError) return null;
    throw error;
  }
}

/**
 * Like `safeParseStoredConditions`, but keeps the group structure and drops
 * only the rows the schema rejects, so one legacy row does not hide every
 * valid row beside it. Group indices are preserved (a group may come back
 * empty). Null when the JSON has no group structure at all.
 */
export function salvageStoredConditions(json: string): StoredConditions | null {
  let groups: unknown[][];

  try {
    groups = parseStoredConditionGroups(json);
  } catch {
    return null;
  }

  return groups.map((group) =>
    group.flatMap((row) => {
      const parsed = StoredConditionRow.safeParse(row);

      return parsed.success ? [parsed.data] : [];
    }),
  );
}

/**
 * Why a condition value (structured, or its stored JSON text) does not match
 * the stored shape, or null when it does.
 */
export function describeStoredConditionsProblem(value: unknown): string | null {
  let parsed = value;

  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return 'the string is not valid JSON';
    }
  }

  const result = StoredConditions.safeParse(parsed);
  if (result.success) return null;

  return result.error.issues
    .slice(0, 3)
    .map((issue) =>
      issue.path.length > 0
        ? `${issue.path.join('.')}: ${issue.message}`
        : issue.message,
    )
    .join('; ');
}
